import { useState, useEffect, useRef } from 'react';
import { Link, useLocation } from 'react-router-dom';
import { useTranslation } from 'react-i18next';

export default function Navbar() {
  const { t } = useTranslation();
  const location = useLocation();
  const [scrolled, setScrolled] = useState(false);
  const [mobileOpen, setMobileOpen] = useState(false);
  const [searchOpen, setSearchOpen] = useState(false);
  const searchRef = useRef<HTMLInputElement>(null);

  useEffect(() => {
    const handleScroll = () => setScrolled(window.scrollY > 60);
    window.addEventListener('scroll', handleScroll, { passive: true });
    return () => window.removeEventListener('scroll', handleScroll);
  }, []);

  useEffect(() => {
    setMobileOpen(false);
  }, [location]);

  useEffect(() => {
    if (searchOpen && searchRef.current) searchRef.current.focus();
  }, [searchOpen]);

  const navLinks = [
    { to: '/', label: t('nav.home') },
    { to: '/category', label: t('nav.category') },
    { to: '/about', label: t('nav.about') },
  ];

  return (
    <nav
      className={`fixed top-0 left-0 right-0 z-50 transition-all duration-500 ${
        scrolled
          ? 'bg-background-50/95 backdrop-blur-md shadow-sm'
          : 'bg-transparent'
      }`}
    >
      <div className="w-full px-4 md:px-6">
        <div className="flex items-center justify-between h-16 md:h-18">
          <Link
            to="/"
            className="flex items-center gap-2 text-foreground-900 hover:text-primary-500 transition-colors duration-300"
          >
            <span className="text-2xl font-heading font-bold tracking-wider">mm131</span>
          </Link>

          <div className="hidden md:flex items-center gap-1">
            {navLinks.map((link) => (
              <Link
                key={link.to}
                to={link.to}
                className={`relative px-4 py-2 text-sm font-medium rounded-full transition-all duration-300 whitespace-nowrap ${
                  location.pathname === link.to
                    ? 'text-primary-600 bg-primary-100/70'
                    : 'text-foreground-700 hover:text-foreground-950 hover:bg-background-200/60'
                }`}
              >
                {link.label}
              </Link>
            ))}
          </div>

          <div className="hidden md:flex items-center gap-3">
            <button
              onClick={() => setSearchOpen(!searchOpen)}
              className="w-9 h-9 flex items-center justify-center rounded-full text-foreground-600 hover:text-foreground-900 hover:bg-background-200/60 transition-all duration-300 cursor-pointer"
              aria-label="搜索"
            >
              <i className="ri-search-line text-lg"></i>
            </button>
          </div>

          <button
            onClick={() => setMobileOpen(!mobileOpen)}
            className="md:hidden w-9 h-9 flex items-center justify-center rounded-full text-foreground-700 hover:bg-background-200/60 transition-all duration-300 cursor-pointer"
            aria-label="菜单"
          >
            <i className={`text-lg ${mobileOpen ? 'ri-close-line' : 'ri-menu-line'}`}></i>
          </button>
        </div>

        {searchOpen && (
          <div className="hidden md:block pb-3 animate-[fadeIn_0.3s_ease]">
            <div className="relative max-w-md mx-auto">
              <i className="ri-search-line absolute left-3 top-1/2 -translate-y-1/2 text-foreground-400 text-sm"></i>
              <input
                ref={searchRef}
                type="text"
                placeholder={t('nav.search')}
                className="w-full pl-9 pr-4 py-2 rounded-full bg-background-100 border border-background-300/60 text-sm text-foreground-900 placeholder:text-foreground-400 focus:outline-none focus:border-primary-400 focus:ring-2 focus:ring-primary-200/50 transition-all duration-300"
              />
            </div>
          </div>
        )}
      </div>

      {mobileOpen && (
        <div className="md:hidden bg-background-50/98 backdrop-blur-md border-t border-background-200/60 animate-[fadeIn_0.3s_ease]">
          <div className="px-4 py-4 space-y-1">
            {navLinks.map((link) => (
              <Link
                key={link.to}
                to={link.to}
                className={`block px-4 py-3 rounded-lg text-sm font-medium transition-all duration-200 whitespace-nowrap ${
                  location.pathname === link.to
                    ? 'text-primary-600 bg-primary-100/70'
                    : 'text-foreground-700 hover:bg-background-200/60'
                }`}
              >
                {link.label}
              </Link>
            ))}
            <div className="pt-2 px-4">
              <div className="relative">
                <i className="ri-search-line absolute left-3 top-1/2 -translate-y-1/2 text-foreground-400 text-sm"></i>
                <input
                  type="text"
                  placeholder={t('nav.search')}
                  className="w-full pl-9 pr-4 py-2.5 rounded-full bg-background-100 border border-background-300/60 text-sm text-foreground-900 placeholder:text-foreground-400 focus:outline-none focus:border-primary-400 transition-all duration-300"
                />
              </div>
            </div>
          </div>
        </div>
      )}
    </nav>
  );
}